import { Button, Modal, Skeleton, Space } from "antd"
import { useContext, useEffect, useReducer } from "react"
import { useCrewQuery, useJobQuery, useSaveVisitMutation, useVisitQuery } from "./apiQueries"
import EditVisitContext from "./EditVisitContext"
import { createEditVisitDataFromVisitId, getJobId, mapStateToRequest, reducer, shouldWarnBeforeDiscardingChanges } from "./editVisitModel"
import JobAndDateSection from "./JobAndDateSection"
import LaborSection from "./LaborSection"
import ItemsSection from "./ItemsSection"
import NotesSection from './NotesSection'
import { buildLaborView, getIdsOfWorkareasWhichHaveTimesheets } from "./labor"
import StatusSection from "./StatusSection"
import { AuthenticationContext } from "../../AuthenticationContext"
import { useCreateVisitForScheduledEventMutation } from "../ScheduleManagement/scheduleQueries"
import { ScheduledEvent } from "../ScheduleManagement/scheduleApi"
import { useJobItemsInvalidator } from '../Items/itemQueries'
import { useInvalidateVisits } from '../../domain/jobs/jobsQueries'
import { Visit } from "./editVisitApi"

export type Props = {
  isOpen: boolean
  visitId: string
  onCloseModal: (changesSaved: boolean) => void
  googleMapsApiKey: string
  defaultLunchBreakMinutes: number
  event?: ScheduledEvent
  jobId?: number
}

const EditVisitModal = ({ isOpen, visitId, jobId, onCloseModal, googleMapsApiKey, defaultLunchBreakMinutes, event }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const visitQuery = useVisitQuery(visitId)
  const crewQuery = useCrewQuery()
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const timeZone = currentUser?.tenant.timeZoneId || browserTimezone
  const [state, dispatch] = useReducer(reducer, createEditVisitDataFromVisitId(visitId, timeZone, event))
  const jobQuery = useJobQuery(getJobId(state, visitQuery.data), visitId)
  const jobItemsInvalidator = useJobItemsInvalidator()
  const invalidateVisits = useInvalidateVisits()

  const saveMutation = useSaveVisitMutation(() => {
    dispatch({ type: "VISIT_ID_CHANGED", payload: visitId, timeZone })
    onCloseModal(true)
    if (state.jobId) {
      jobItemsInvalidator.invalidateForJob(state.jobId)
      invalidateVisits(state.jobId)
    }
  })

  const createVisitForScheduledEventMutation = useCreateVisitForScheduledEventMutation( () => {
    onCloseModal(true)
  })

  useEffect(() => {
    if (!isOpen) {
      return
    }

    dispatch({ type: "VISIT_ID_CHANGED", payload: visitId, timeZone, jobId })

    if (visitQuery.data?.visitDate) {
      dispatch({ type: 'BULK_DATE_CHANGED', payload: visitQuery.data.visitDate })
    }

    if(event?.jobId) {
      dispatch({ type: "JOB_ID_CHANGED", payload: event.jobId })
      dispatch({ type: "VISIT_DATE_CHANGED", payload: new Date().toISOString() })
    }
  }, [visitId, visitQuery.data?.visitDate, isOpen])

  const buildContent = () => {
    if (visitQuery.isLoading) {
      return <Skeleton active style={{ minWidth: '600px' }} />
    }
    return <>
      <JobAndDateSection timeZone={timeZone} />
      <LaborSection timeZone={timeZone} />
      <ItemsSection />
      <StatusSection />
      <NotesSection />
    </>
  }

  const isLoading = visitQuery.isLoading || crewQuery.isLoading;
  const emptyVisit: Visit = { id: '', jobId: 0, visitDate: '', timesheets: [] }
  const laborView = isLoading
    ? []
    : buildLaborView(state.laborChanges, visitQuery.data ?? emptyVisit, crewQuery.data ?? [], timeZone, state.visitDate)
  const cannotBeSubmitted = getJobId(state, visitQuery.data) === undefined || laborView.some(x => x.cannotBeSubmitted) || saveMutation.isLoading || isLoading

  const confirmCancel = () => {
    if (shouldWarnBeforeDiscardingChanges(state)) {
      Modal.confirm({
        title: 'Discard Changes?',
        content: 'Do you want to discard the changes you have made to this visit?',
        okText: 'Discard',
        cancelText: 'Keep Editing',
        onOk: cancel
      })
    } else {
      cancel()
    }
  }

  const cancel = () => {
    dispatch({ type: "VISIT_ID_CHANGED", payload: visitId, timeZone })
    onCloseModal(false)
  }

  const onSave = (submitTimesheets: boolean) => {
    const visit = visitQuery.data ?? emptyVisit
    const workareaIds = getIdsOfWorkareasWhichHaveTimesheets(visit, state.laborChanges)
    if(event?.id) {
      createVisitForScheduledEventMutation.mutate({eventId: event.id, request: mapStateToRequest(state, visit, submitTimesheets, jobQuery.data!, workareaIds)})
    } else {
      saveMutation.mutate(mapStateToRequest(state, visit, submitTimesheets, jobQuery.data!, workareaIds))
    }
  }

  return <EditVisitContext.Provider value={{ ...state, dispatch, googleMapsApiKey, laborView, defaultLunchBreakMinutes }}>
    <Modal
      open={isOpen}
      onCancel={confirmCancel}
      footer={<>
        <Button onClick={cancel}>Discard Changes</Button>
        <Button type="primary" disabled={cannotBeSubmitted} onClick={() => onSave(false)} loading={saveMutation.isLoading}>
          Save
        </Button>
        <Button type="primary" disabled={cannotBeSubmitted} onClick={() => onSave(true)} loading={saveMutation.isLoading}>
          Save and Submit
        </Button>
      </>}
      title={state.isNewVisit ? 'Add New Visit' : 'Edit Visit'}
      width={900}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {buildContent()}
      </Space>
    </Modal>
  </EditVisitContext.Provider>
}
export default EditVisitModal
